import React, { useState, useEffect } from 'react';
import { galleryImages } from '../mock';
import { Droplets, Sparkles, Car, X, ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const sections = [
    { id: 'snow-foam', title: 'Foamy Wash', description: 'Deep-cleaning snow foam treatments that lift dirt before contact.', icon: Droplets },
    { id: 'interior', title: 'Interior Detail', description: 'Spotless cabins, conditioned leather, and freshly vacuumed carpets.', icon: Sparkles },
    { id: 'exterior', title: 'Exterior Detail', description: 'Glossy finishes and showroom-ready exteriors.', icon: Car }
  ];

  const openLightbox = (img) => {
    const idx = galleryImages.findIndex(i => i.id === img.id);
    setLightboxIndex(idx);
  };
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = (e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i + 1) % galleryImages.length);
  };
  const prevImage = (e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [lightboxIndex]);

  const activeImage = lightboxIndex !== null ? galleryImages[lightboxIndex] : null;

  return (
    <section id="gallery" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-700">Our Recent Work</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            Results that <span className="text-blue-600">speak for themselves.</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            From foamy pre-washes to mirror-finish exteriors — every detail counts.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-24">
          {sections.map((section) => {
            const sectionImages = galleryImages.filter(img => img.category === section.id);
            if (sectionImages.length === 0) return null;
            const Icon = section.icon;

            return (
              <div key={section.id} data-testid={`gallery-section-${section.id}`}>
                {/* Subheading */}
                <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900">{section.title}</h3>
                      <p className="text-slate-600 mt-1">{section.description}</p>
                    </div>
                  </div>
                  <div className="text-sm font-bold text-blue-700 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full">
                    {sectionImages.length} {sectionImages.length === 1 ? 'photo' : 'photos'}
                  </div>
                </div>

                <div className="h-px bg-gradient-to-r from-blue-200 via-slate-200 to-transparent mb-8"></div>

                {/* Images Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sectionImages.map((image) => (
                    <button
                      key={image.id}
                      data-testid={`gallery-img-${image.id}`}
                      onClick={() => openLightbox(image)}
                      className="group relative aspect-[4/5] overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 cursor-pointer"
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                          <div className="inline-block bg-blue-500/90 backdrop-blur-md text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-2 uppercase tracking-wider">
                            View
                          </div>
                          <p className="text-white font-bold text-lg leading-snug">{image.alt}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-20 p-10 bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl border border-slate-100">
          <p className="text-slate-700 text-lg mb-2">Want to see more transformations?</p>
          <a
            href="https://instagram.com/proclean_mobiledetailing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-bold text-xl transition-colors"
          >
            Follow @proclean_mobiledetailing on Instagram
            <ChevronRight className="w-5 h-5 ml-1" />
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {activeImage && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={prevImage}
            className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={nextImage}
            className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={activeImage.url}
              alt={activeImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <p className="text-white font-semibold mt-4 text-center">{activeImage.alt}</p>
            <p className="text-slate-400 text-sm mt-1">{lightboxIndex + 1} of {galleryImages.length}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
